import React from 'react';
import { useAppStore } from '@/store/appStore';
import type { GreekBrainFeature } from '@/types';

const FEATURE_ORDER: GreekBrainFeature[] = ['voice_dialogue', 'picture_eval', 'ai_tutor', 'writing_eval'];

const FEATURE_LABELS: Record<GreekBrainFeature, string> = {
    voice_dialogue: 'Voice dialogues',
    picture_eval: 'Picture descriptions',
    ai_tutor: 'AI tutor',
    writing_eval: 'Writing checks',
};

/**
 * Shows the user's current monthly usage vs. quota per GreekBrain AI feature.
 * Reads from the store user payload (greekbrain_usage). Used in the paywall and
 * the subscription-management modal.
 */
export const GreekBrainUsageList: React.FC<{ title?: string }> = ({ title }) => {
    const usage = useAppStore((s) => s.user?.greekbrain_usage);
    const isSubscribed = useAppStore((s) => s.user?.is_greekbrain_subscribed);
    // Monthly quotas only apply to subscribers — showing them to free/trial users
    // is confusing, so the list is hidden until the user is subscribed.
    if (!usage || !isSubscribed) return null;

    return (
        <div>
            {title && (
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
            )}
            <div className="space-y-2.5">
                {FEATURE_ORDER.map((feature) => {
                    const row = usage[feature];
                    if (!row) return null;
                    const { used, limit } = row;
                    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                    const atLimit = used >= limit && limit > 0;
                    return (
                        <div key={feature}>
                            <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-gray-600 font-medium">{FEATURE_LABELS[feature]}</span>
                                <span className={`font-semibold ${atLimit ? 'text-red-500' : 'text-gray-500'}`}>
                                    {used} / {limit}
                                </span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                <div
                                    className={`h-full rounded-full ${atLimit ? 'bg-red-400' : 'bg-primary'}`}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            <p className="text-[11px] text-gray-400 mt-2">Resets at the start of each month.</p>
        </div>
    );
};
